import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tenantsService } from '../services/tenants.service';
import {
  AgencyOwnDocument,
  AgencyRoom,
  getTenantStatusLabel,
  statutDuBail,
  TENANT_DOCUMENT_TYPE_LABELS,
  UPLOADABLE_DOCUMENT_TYPES,
} from '../types/tenant';
import { getErrorMessage } from '../utils/error';
import { formatDate } from '../utils/format';
import MentionBail from '../components/MentionBail';
import '../styles/CandidatsPage.css';
import '../styles/ContactsAgency.css';

/**
 * Espace agence — dépôt d'une pièce dans le dossier d'un locataire.
 *
 * PAGE et non repli, comme la fiche d'agence. Le formulaire tenait dans une
 * cellule de tableau : c'est le motif qui a produit quatre défauts sur l'écran
 * Agences.
 *
 * Ce que l'agence voit ici reste minimisé : le lot, l'identité de l'occupant,
 * les dates de bail — et SES PROPRES DÉPÔTS, rien d'autre du dossier. Ni loyer,
 * ni charges, ni contact, ni le bail, ni les pièces de candidature (§6ter).
 *
 * Relire et retirer ses dépôts a été ouvert le 2026-08-30 : jusque-là un dépôt
 * était définitif ET invisible, si bien qu'une agence qui se trompait de fichier
 * ne pouvait ni le constater ni le corriger.
 *
 * Aucune route API dédiée : `GET /api/tenants/agency/rooms` renvoie déjà tout,
 * la liste est courte, et le backend y borne le périmètre.
 */
const AgencyDocumentDropPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();

  const [room, setRoom] = useState<AgencyRoom | null>(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [miennes, setMiennes] = useState<AgencyOwnDocument[]>([]);
  const [retraitId, setRetraitId] = useState<string | null>(null);

  const [docType, setDocType] = useState('INVENTORY_ENTRY');
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const lots = await tenantsService.listAgencyRooms();
      const trouve = lots.find((r) => r.roomId === roomId) ?? null;
      setRoom(trouve);
      setIntrouvable(trouve === null);
      setMiennes(
        trouve?.tenantInfoId
          ? await tenantsService.listOwnAgencyDocuments(trouve.tenantInfoId)
          : [],
      );
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement'));
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    charger();
  }, [charger]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!room?.tenantInfoId || !file) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await tenantsService.uploadDocument(
        room.tenantInfoId, file, docType, notes || undefined,
      );
      setFile(null);
      setNotes('');
      setNotice('Document déposé. Le propriétaire en est informé.');
      setMiennes(await tenantsService.listOwnAgencyDocuments(room.tenantInfoId));
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du dépôt du document'));
    } finally {
      setBusy(false);
    }
  };

  const telecharger = async (doc: AgencyOwnDocument) => {
    if (!room?.tenantInfoId) return;
    try {
      const blob = await tenantsService.downloadDocument(room.tenantInfoId, doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du téléchargement'));
    }
  };

  const retirer = async (doc: AgencyOwnDocument) => {
    if (!room?.tenantInfoId) return;
    if (!window.confirm(
      `Retirer « ${doc.fileName} » ? Le fichier est supprimé définitivement, `
      + 'et le propriétaire en est informé.',
    )) return;
    setRetraitId(doc.id);
    setError(null);
    setNotice(null);
    try {
      await tenantsService.deleteDocument(room.tenantInfoId, doc.id);
      setMiennes(await tenantsService.listOwnAgencyDocuments(room.tenantInfoId));
      setNotice('Document retiré. Le propriétaire en est informé.');
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du retrait du document'));
    } finally {
      setRetraitId(null);
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;

  // Un lot sans occupant n'a pas de dossier : le dire, plutôt que d'afficher un
  // formulaire dont l'envoi échouerait.
  if (introuvable || !room || !room.tenantInfoId) {
    return (
      <div className="candidats-page">
        <div className="socle-vide">
          <p className="socle-vide-titre">
            {introuvable || !room ? 'Lot introuvable' : 'Ce lot est libre'}
          </p>
          <p className="socle-vide-aide">
            {introuvable || !room
              ? "Il ne vous est plus partagé, ou l'accès a été suspendu."
              : "Il n'a pas d'occupant, donc aucun dossier où déposer une pièce."}
          </p>
          <button
            className="btn-action btn-secondary"
            onClick={() => navigate('/agency/documents')}
          >
            Retour aux lots
          </button>
        </div>
      </div>
    );
  }

  const occupant = `${room.firstName || ''} ${room.lastName || ''}`.trim();

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <div>
          <button
            className="btn-action btn-secondary"
            style={{ marginBottom: 8 }}
            onClick={() => navigate('/agency/documents')}
          >
            ← Lots
          </button>
          <h2>{room.roomName}</h2>
          <p className="access-hint" style={{ margin: 0 }}>
            {occupant || 'Occupant'} — {room.colocName}
            {' '}
            <span className={`tenant-status-badge status-${statutDuBail(room.noticeDate, room.leaseStartDate)}`}>
              {getTenantStatusLabel(statutDuBail(room.noticeDate, room.leaseStartDate))}
            </span>
          </p>
        </div>
      </div>

      {error && <div className="candidats-error">{error}</div>}
      {notice && <div className="candidats-notice">{notice}</div>}

      <div className="cx-detail">
        <div className="cx-card">
          <h4 className="cx-title">Le bail</h4>
          <div className="cx-row">
            <span className="cx-label">Entrée</span>
            <span className="cx-value">
              {room.leaseStartDate ? formatDate(room.leaseStartDate) : '—'}
            </span>
          </div>
          <div className="cx-row">
            <span className="cx-label">Préavis</span>
            <span className="cx-value">
              {room.noticeDate ? formatDate(room.noticeDate) : '—'}
            </span>
          </div>
          <div className="cx-row">
            <span className="cx-label">Sortie prévue</span>
            <span className="cx-value">
              {room.noticeExpectedExitDate
                ? formatDate(room.noticeExpectedExitDate)
                : '—'}
            </span>
          </div>
          <div className="cx-row">
            <span className="cx-label">Fin de bail</span>
            <span className="cx-value">
              {room.leaseEndDate ? formatDate(room.leaseEndDate) : '—'}
            </span>
          </div>
          <MentionBail />
        </div>

        <div className="cx-card">
          <h4 className="cx-title">Déposer une pièce</h4>
          <p className="access-hint">
            Le propriétaire est informé de chaque dépôt, et chacun est journalisé.
            Vous ne voyez que vos propres dépôts, jamais le reste du dossier.
          </p>
          {/* Le nom des pièces attendues, rien de leur contenu. C'est ce qui
              permet à l'agence d'aller les chercher sans rien apprendre du
              dossier. */}
          {/* Le bail n'a pas commencé : le dépôt reste possible — le bail
              lui-même, l'acte de cautionnement arrivent souvent avant — mais
              l'agence doit savoir que la personne n'a pas emménagé. */}
          {!room.bailCommence && (
            <div className="cx-context" style={{ marginTop: 0, marginBottom: 16 }}>
              <strong>Ce lot est réservé.</strong> Le bail commence
              {room.leaseStartDate ? ` le ${formatDate(room.leaseStartDate)}` : ' plus tard'} :
              l&apos;occupant n&apos;a pas encore emménagé. Vous pouvez déposer dès
              maintenant, rien ne vous est réclamé avant cette date.
            </div>
          )}
          {room.dossierSuivi && room.dossierManques.length > 0 && (
            <div className="cx-context" style={{ marginTop: 0, marginBottom: 16 }}>
              <strong>Il manque à ce dossier :</strong>{' '}
              {room.dossierManques.join(' · ')}
            </div>
          )}
          <form onSubmit={handleUpload}>
            <div className="action-form-grid action-form-grid--egal">
              <div className="action-field">
                <label htmlFor="doc-type">Type de document</label>
                <select
                  id="doc-type"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                >
                  {UPLOADABLE_DOCUMENT_TYPES.map((dt) => (
                    <option key={dt} value={dt}>
                      {TENANT_DOCUMENT_TYPE_LABELS[dt]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="action-field">
                <label htmlFor="doc-file">Fichier</label>
                <input
                  id="doc-file"
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  required
                />
              </div>
              <div className="action-field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="doc-notes">Notes (optionnel)</label>
                <input
                  id="doc-notes"
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              <div className="cx-actions">
                <button
                  type="submit"
                  className="btn-action btn-save"
                  disabled={busy || !file}
                >
                  {busy ? 'Dépôt en cours…' : 'Déposer'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Ses propres dépôts — et rien d'autre du dossier. Sans cette liste,
            un mauvais fichier était invisible et définitif. */}
        <div className="cx-card">
          <h4 className="cx-title">Vos dépôts sur ce lot</h4>
          {miennes.length === 0 ? (
            <p className="timeline-empty">
              Vous n'avez encore rien déposé ici. Le dossier peut contenir
              d'autres pièces, que vous ne voyez pas.
            </p>
          ) : (
            <div className="depot-liste">
              {miennes.map((doc) => (
                <div key={doc.id} className="depot-item">
                  <span className="depot-infos">
                    <span className="depot-nom" title={doc.fileName}>
                      {doc.fileName}
                    </span>
                    <span className="depot-meta">
                      {TENANT_DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                      {doc.uploadedAt ? ` · ${formatDate(doc.uploadedAt)}` : ''}
                    </span>
                  </span>
                  <span className="depot-actions">
                    <button
                      className="btn-action btn-download"
                      onClick={() => telecharger(doc)}
                    >
                      Télécharger
                    </button>
                    <button
                      className="btn-action btn-danger"
                      disabled={retraitId === doc.id}
                      onClick={() => retirer(doc)}
                    >
                      {retraitId === doc.id ? 'Retrait…' : 'Retirer'}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyDocumentDropPage;
